import { DatePipe, JsonPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PanelModule } from 'primeng/panel';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { TestModeService } from '../../../services/test-mode.service';

import { CesTooltipAutoDismissDirective } from '../../../directives/ces-tooltip-auto-dismiss.directive';
/**
 * State change event emitted by the debug panel when a control flag is toggled.
 */
export interface HourpickerDebugPanelStateChange {
  property: 'pristine' | 'dirty' | 'touched' | 'valid' | 'disabled';
  value: boolean;
}

@Component({
  selector: 'ces-hourpicker-field-debug-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    DatePipe,
    JsonPipe,
    NgClass,
    PanelModule,
    TabViewModule,
    ButtonModule,
    TooltipModule,
    ChipModule,
    DividerModule,
    CardModule,
    OverlayPanelModule,
    FormsModule,
    InputTextModule,
    CheckboxModule,
  ],
  templateUrl: './ces-hourpicker-field-debug-panel.component.html',
  styleUrl: './ces-hourpicker-field-debug-panel.component.scss',
})
export class CesHourpickerFieldDebugPanelComponent {
  Object = Object;

  @Input() value: string | null = null;
  @Input() errors: Record<string, boolean> = {};
  @Input() errorMessages: string[] = [];
  @Input() externalErrors: Record<string, string> | undefined;
  @Input() pristine: boolean = true;
  @Input() dirty: boolean = false;
  @Input() touched: boolean = false;
  @Input() valid: boolean = true;
  @Input() disabled: boolean = false;

  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() showSeconds: boolean = true;
  @Input() minTime: string | undefined;
  @Input() maxTime: string | undefined;
  @Input() floatLabel: boolean = false;
  @Input() collapsed: boolean = true;
  @Input() toggleable: boolean = true;
  @Input() panelTitle: string = 'Hourpicker properties';

  @Output() stateChange = new EventEmitter<HourpickerDebugPanelStateChange>();

  @Output() valueChange = new EventEmitter<string | null>();
  @Output() errorsChange = new EventEmitter<Record<string, boolean>>();
  @Output() errorMessagesChange = new EventEmitter<string[]>();
  @Output() externalErrorsChange = new EventEmitter<Record<string, string> | undefined>();
  @Output() eventTriggered = new EventEmitter<{ name: string }>();

  simpleValueInput: string = '';
  newErrorKey: string = '';
  newErrorValue: string = '';
  eventLog: { name: string; time: Date }[] = [];

  timePresets: string[] = ['09:00', '12:00', '14:00', '17:00', '18:00', '23:59'];

  protected readonly t = inject(TestModeService);
  private readonly translateService = inject(TranslocoService);

  markAsPristine(): void {
    this.pristine = true;
    this.dirty = false;
    this.stateChange.emit({ property: 'pristine', value: true });
    this.stateChange.emit({ property: 'dirty', value: false });
  }

  markAsDirty(): void {
    this.pristine = false;
    this.dirty = true;
    this.stateChange.emit({ property: 'pristine', value: false });
    this.stateChange.emit({ property: 'dirty', value: true });
  }

  markAsTouched(): void {
    this.touched = true;
    this.stateChange.emit({ property: 'touched', value: true });
  }

  markAsUntouched(): void {
    this.touched = false;
    this.stateChange.emit({ property: 'touched', value: false });
  }

  setValidity(isValid: boolean): void {
    this.valid = isValid;
    this.stateChange.emit({ property: 'valid', value: isValid });

    if (isValid && Object.keys(this.errors || {}).length > 0) {
      this.errors = {};
      this.errorMessages = [];
      this.errorsChange.emit(this.errors);
      this.errorMessagesChange.emit(this.errorMessages);
    }
  }

  setDisabled(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChange.emit({ property: 'disabled', value: isDisabled });
  }

  resetState(): void {
    this.markAsPristine();
    this.markAsUntouched();
    this.setValidity(true);
    this.setDisabled(false);
  }

  getValueTooltip(): string {
    return this.value ? `Heure: ${this.value}` : 'Aucune heure sélectionnée';
  }

  setNullValue(): void {
    this.value = null;
    this.valueChange.emit(this.value);
    this.markAsDirty();
  }

  setCurrentTime(): void {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');

    if (this.showSeconds) {
      const seconds = now.getSeconds().toString().padStart(2, '0');
      this.value = `${hours}:${minutes}:${seconds}`;
    } else {
      this.value = `${hours}:${minutes}`;
    }

    this.valueChange.emit(this.value);
    this.markAsDirty();
  }

  setPresetTime(time: string): void {
    this.value = time;
    this.valueChange.emit(this.value);
    this.markAsDirty();
  }

  updateSimpleValue(): void {
    if (!this.simpleValueInput.trim()) {
      this.setNullValue();
      return;
    }

    const timeRegex = this.showSeconds ? /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9]):([0-5]?[0-9])$/ : /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9])$/;

    if (!timeRegex.test(this.simpleValueInput)) {
      console.warn(this.translateService.translate('common.errors.invalidTimeFormat'));
      return;
    }

    this.value = this.simpleValueInput;
    this.valueChange.emit(this.value);
    this.markAsDirty();
  }

  hasErrors(): boolean {
    return (
      ((this.errorMessages && this.errorMessages.length > 0) ||
        (this.errors && Object.keys(this.errors).length > 0) ||
        (this.externalErrors && Object.keys(this.externalErrors).length > 0)) ??
      false
    );
  }

  getErrorEntries(): Array<{ key: string; value: boolean }> {
    if (!this.errors) return [];
    return Object.entries(this.errors).map(([key, value]) => ({ key, value }));
  }

  getExternalErrorEntries(): Array<{ key: string; value: string }> {
    if (!this.externalErrors) return [];
    return Object.entries(this.externalErrors).map(([key, value]) => ({ key, value }));
  }

  removeErrorMessage(message: string): void {
    this.errorMessages = this.errorMessages.filter(msg => msg !== message);
    this.errorMessagesChange.emit(this.errorMessages);
    this.updateValidityFromErrors();
  }

  removeError(key: string): void {
    if (this.errors && key in this.errors) {
      const newErrors = { ...this.errors };
      delete newErrors[key];
      this.errors = newErrors;
      this.errorsChange.emit(this.errors);
      this.updateValidityFromErrors();
    }
  }

  removeExternalError(key: string): void {
    if (this.externalErrors && key in this.externalErrors) {
      const newExternalErrors = { ...this.externalErrors };
      delete newExternalErrors[key];
      this.externalErrors = newExternalErrors;
      this.externalErrorsChange.emit(this.externalErrors);
      this.updateValidityFromErrors();
    }
  }

  clearAllErrors(): void {
    this.errors = {};
    this.errorMessages = [];
    this.externalErrors = {};
    this.errorsChange.emit(this.errors);
    this.errorMessagesChange.emit(this.errorMessages);
    this.externalErrorsChange.emit(this.externalErrors);
    this.setValidity(true);
  }

  addError(): void {
    if (!this.newErrorKey) return;

    try {
      const raw = this.newErrorValue;
      let errorValue: unknown;
      if (raw === 'true') {
        errorValue = true;
      } else if (raw === 'false') {
        errorValue = false;
      } else if (raw === 'null') {
        errorValue = null;
      } else {
        const numeric = Number(raw);
        errorValue = Number.isNaN(numeric) ? raw : numeric;
      }

      this.errors = {
        ...this.errors,
        [this.newErrorKey]: Boolean(errorValue) || true,
      };

      if (!this.errorMessages.includes(`Erreur: ${this.newErrorKey}`)) {
        this.errorMessages = [...this.errorMessages, `Erreur: ${this.newErrorKey}`];
      }

      this.errorsChange.emit(this.errors);
      this.errorMessagesChange.emit(this.errorMessages);
      this.updateValidityFromErrors();

      this.newErrorKey = '';
      this.newErrorValue = '';
    } catch (e) {
      console.error(this.translateService.translate('common.errors.errorAddingError'), e);
    }
  }

  updateValidityFromErrors(): void {
    const hasAnyErrors =
      (this.errorMessages && this.errorMessages.length > 0) ||
      (this.errors && Object.keys(this.errors).length > 0) ||
      (this.externalErrors && Object.keys(this.externalErrors).length > 0);

    this.valid = !hasAnyErrors;
    this.stateChange.emit({ property: 'valid', value: this.valid });
  }

  triggerEvent(eventName: string): void {
    this.eventLog.unshift({
      name: eventName,
      time: new Date(),
    });

    if (this.eventLog.length > 50) {
      this.eventLog = this.eventLog.slice(0, 50);
    }

    if (eventName === 'focus') {
    } else if (eventName === 'blur') {
      this.markAsTouched();
    } else if (eventName === 'change') {
      this.markAsDirty();
    }

    this.eventTriggered.emit({ name: eventName });
  }

  clearEventLog(): void {
    this.eventLog = [];
  }
}
